import { useCallback, useEffect, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { setWorkspaceBranding } from 'src/utils/workspace-logo'
import {
  shopBrandingGetService,
  shopBrandingUpdateService,
  shopCustomCodeUpdateService,
} from 'src/api/shop'
import safeTrack from 'src/utils/safe-track'

export const DEFAULT_PRIMARY_COLOR = '#015FFB'
export const DEFAULT_FONT = 'Inter'

const EMPTY = {
  logo: null,
  favicon: null,
  primaryColor: null,
  font: null,
  customCss: '',
  customJs: '',
}

// Branding fields as Amplitude `setting` values; color and font also send
// the value.
const TRACKED = {
  logo: { setting: 'logo' },
  favicon: { setting: 'favicon' },
  primaryColor: { setting: 'primary_color', withValue: true },
  font: { setting: 'font', withValue: true },
}

const trackBranding = (previous, patch) =>
  Object.keys(patch).forEach(key => {
    const field = TRACKED[key]
    if (!field || (previous[key] || null) === (patch[key] || null)) return
    let action = 'changed'
    if (!previous[key]) action = 'added'
    else if (!patch[key]) action = 'removed'
    safeTrack('changed_branding', {
      setting: field.setting,
      action,
      ...(field.withValue && { value: patch[key] || null }),
    })
  })

const errorMessage = (error, fallback) =>
  error?.response?.data?.operationMessage || fallback

/**
 * Loads the workspace branding and saves it.
 * - `save(patch)`: logo / favicon / primaryColor / font, auto-saved on every
 *   change (the API takes the whole set, so the patch is merged first).
 * - `saveCustomCode({ css, js })`: the custom code card's own Save.
 * Both show the "Saved" toast; a failed auto-save rolls the field back.
 */
const useBranding = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [branding, setBranding] = useState(EMPTY)
  const [loading, setLoading] = useState(true)
  // Latest saved values, so overlapping auto-saves merge correctly.
  const savedRef = useRef(EMPTY)

  useEffect(() => {
    let cancelled = false
    shopBrandingGetService()
      .then(response => {
        if (cancelled) return
        const next = { ...EMPTY, ...(response?.data || {}) }
        next.customCss = next.customCss || ''
        next.customJs = next.customJs || ''
        savedRef.current = next
        setBranding(next)
      })
      .catch(error =>
        enqueueSnackbar(errorMessage(error, 'Could not load branding.'), {
          variant: 'error',
        }),
      )
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [enqueueSnackbar])

  const save = useCallback(
    patch => {
      const previous = savedRef.current
      const next = { ...previous, ...patch }
      savedRef.current = next
      setBranding(current => ({ ...current, ...patch }))

      return shopBrandingUpdateService({
        logo: next.logo,
        favicon: next.favicon,
        primaryColor: next.primaryColor,
        font: next.font,
      })
        .then(() => {
          // The workspace switcher and previews show it right away.
          setWorkspaceBranding({
            logo: next.logo,
            favicon: next.favicon,
            primaryColor: next.primaryColor,
            font: next.font,
          })
          trackBranding(previous, patch)
          enqueueSnackbar('Saved', { variant: 'success' })
        })
        .catch(error => {
          const rollback = Object.fromEntries(
            Object.keys(patch).map(key => [key, previous[key]]),
          )
          savedRef.current = { ...savedRef.current, ...rollback }
          setBranding(current => ({ ...current, ...rollback }))
          enqueueSnackbar(errorMessage(error, 'Could not save branding.'), {
            variant: 'error',
          })
        })
    },
    [enqueueSnackbar],
  )

  const saveCustomCode = useCallback(
    ({ css, js }) =>
      shopCustomCodeUpdateService({ css, js })
        .then(() => {
          const patch = { customCss: css, customJs: js }
          savedRef.current = { ...savedRef.current, ...patch }
          setBranding(current => ({ ...current, ...patch }))
          safeTrack('saved_custom_code', {
            has_css: Boolean(css?.trim()),
            has_js: Boolean(js?.trim()),
          })
          enqueueSnackbar('Saved', { variant: 'success' })
          return true
        })
        .catch(error => {
          enqueueSnackbar(errorMessage(error, 'Could not save custom code.'), {
            variant: 'error',
          })
          return false
        }),
    [enqueueSnackbar],
  )

  return { branding, loading, save, saveCustomCode }
}

export default useBranding
